import React, { FunctionComponent } from 'react';

import { SummaryInformationCard } from 'src/components/summary-information-card';
import { useInitializeShoppingCart } from 'src/hooks/use-initialize-shopping-cart';
import { removePurchaseFromCart, removeUpgradeFromCart } from 'src/state/cart/cart-slice';
import { selectItemQuantitiesFromCart } from 'src/state/cart/selectors';
import { useAppDispatch, useAppSelector } from 'src/state/thunk-api';
import { getOrThrow } from 'src/utils/type-utils';
import { i18n } from 'src/i18n';
import { NavigationService } from 'src/services/navigation-service';
import { currencyWithMonthlySuffix, currencyWithSuffix } from 'src/utils/currency-utils';
import { useProductsResponseQueryStateOrThrow } from 'src/hooks/use-products-query';

import { ProductInformation } from './product-information';

export const ProductsSummary: FunctionComponent = () => {
  const itemQuantitiesFromCart = useAppSelector(selectItemQuantitiesFromCart);
  const { shoppingCart } = useInitializeShoppingCart();
  const { newDecoder, upgradeDecoder } = useProductsResponseQueryStateOrThrow();
  const dispatch = useAppDispatch();

  const {
    totalPrices: { monthlyPrice, onceOnlyPrice },
  } = getOrThrow(shoppingCart, 'Invalid state. Shopping cart must be present');
  const { quantityUpgrade, quantityPurchase } = itemQuantitiesFromCart;

  const renderContent = () => (
    <div>
      {quantityPurchase > 0 && newDecoder && (
        <ProductInformation
          count={quantityPurchase}
          name={i18n.page.productSelect.name}
          linkToEdit={NavigationService.PRODUCT_PURCHASE}
          onRemove={() => dispatch(removePurchaseFromCart())}
          setupCost={currencyWithSuffix(newDecoder.onceOnlyPrice)}
          setupCostLabel={i18n.page.summary.productInformation.setupCost}
          monthlyCost={currencyWithMonthlySuffix(newDecoder.monthlyPrice)}
        />
      )}
      {quantityUpgrade > 0 && upgradeDecoder && (
        <ProductInformation
          count={quantityUpgrade}
          name={i18n.page.upgradeSeveral.name}
          linkToEdit={NavigationService.PRODUCT_UPGRADE}
          onRemove={() => dispatch(removeUpgradeFromCart())}
          setupCost={currencyWithSuffix(upgradeDecoder.onceOnlyPrice)}
          setupCostLabel={i18n.page.summary.productInformation.setupCost}
          monthlyCost={i18n.page.summary.productInformation.noMonthlyCost}
        />
      )}
    </div>
  );

  return (
    <SummaryInformationCard
      title={i18n.page.summary.productInformation.header}
      content={renderContent()}
      summaryItems={[
        {
          label: i18n.page.summary.productInformation.monthlyCostIncrease,
          value: currencyWithSuffix(monthlyPrice),
        },
        {
          label: i18n.page.summary.productInformation.totalSetupCost,
          value: currencyWithSuffix(onceOnlyPrice),
        },
      ]}
    />
  );
};
